import React, { useRef, useCallback } from 'react'
import { Box, Link, Stack, Typography } from '@mui/material'
import ArrowLink from '../shared/ArrowLink'
import SectionWrapper from '../layout/SectionWrapper'
import apps from '../../data/apps'
import { moreAppsLink } from '../../data/info'

const Apps = () => {
  return (
    <SectionWrapper
      title={'Cases de sucesso'}
      subtitle={'São mais de 350 aplicativos criados na Academy e muitos deles ultrapassaram nossas quatro paredes, chegaram ao público e conquistaram  reconhecimentos importantes do mercado de tecnologia!'}
      description={
        <Box sx={{ textAlign: { sm: 'center', xs: 'flex-start' } }}>
          <ArrowLink link={moreAppsLink} sx={{ justifyContent: { sm: 'center', xs: 'flex-start' }, marginTop: 1 }}>
            <Box sx={{ display: { sm: 'inherit', xs: 'none' } }}>
              Conheça mais sobre o processo de desenvolvimento dos nossos apps
            </Box>
            <Box sx={{ display: { sm: 'none', xs: 'inherit' } }}>Conheça mais apps</Box>
          </ArrowLink>
        </Box>
      }
      backgroundColor={'#F2F2F2'}
      pt={{ md: 8, xs: 5.5 }}
      pb={{ md: 6, xs: 5 }}
    >
      <Stack
        direction="row"
        justifyContent={{ md: 'space-evenly', xs: 'inherit' }}
        spacing={{ md: 0, sm: 5, xs: 0 }}
        mt={2}
        py={3}
        px={{ md: 2, xs: '12.5vw' }}
        sx={{
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          width: '100%',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        {apps.map((app) => (
          <AppTile key={app.name} app={app} />
        ))}
      </Stack>
    </SectionWrapper>
  )
}

const AppTile = ({ app }) => {
  const isDisabled = false
  const cardRef = useRef(null)
  const dragStartPos = useRef(null)
  const DRAG_THRESHOLD = 5

  const handlePointerDown = useCallback((e) => {
    dragStartPos.current = { x: e.clientX, y: e.clientY }
  }, [])

  const handleClick = useCallback((e) => {
    if (isDisabled || !app.link) return
    if (dragStartPos.current) {
      const dx = Math.abs(e.clientX - dragStartPos.current.x)
      const dy = Math.abs(e.clientY - dragStartPos.current.y)
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
        e.preventDefault()
        return
      }
    }
    window.open(app.link, '_blank')
  }, [app.link, isDisabled])

  const handleMouseMove = (e) => {
    if (isDisabled) return
    const card = cardRef.current
    if (!card) return
    const rect = card.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const glares = card.querySelectorAll('.glare-overlay')
    glares.forEach(glare => {
      const gRect = glare.getBoundingClientRect()
      const gX = e.clientX - gRect.left
      const gY = e.clientY - gRect.top
      const pctX = (gX / gRect.width) * 100
      const pctY = (gY / gRect.height) * 100
      glare.style.background = `radial-gradient(circle at ${pctX}% ${pctY}%, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.0) 60%)`
      glare.style.opacity = '1'
    })
  }

  const handleMouseLeave = () => {
    if (isDisabled) return
    const card = cardRef.current
    if (!card) return
    const glares = card.querySelectorAll('.glare-overlay')
    glares.forEach(glare => {
      glare.style.opacity = '0'
    })
  }

  const ImageContent = (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        width: { sm: '204.6px', xs: '60vw' },
        transition: 'all 0.3s ease',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        filter: isDisabled ? 'grayscale(100%) opacity(0.6)' : 'none',
        '&:hover': isDisabled ? {
          animation: 'shake 0.4s ease-in-out'
        } : {
          transform: 'scale(1.04)',
          filter: 'drop-shadow(0px 12px 24px rgba(0,0,0,0.15))'
        }
      }}
    >
      <Box
        component="img"
        src={app.page}
        alt={`Screenshot do app ${app.name}`}
        sx={{
          width: { sm: 'auto', xs: '100%' },
          height: { sm: '444px', xs: 'auto' },
          borderRadius: '24px',
        }}
      />
      <Box
        sx={{
          width: '96px',
          height: '96px',
          position: 'absolute',
          bottom: { md: '47px', xs: '-44px' },
          right: { md: '-34px', xs: 0 },
          left: { md: 'inherit', xs: 0 },
          marginX: 'auto',
          borderRadius: '22%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 4,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={app.icon}
          alt={`Ícone do app ${app.name}`}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        {!isDisabled && (
          <Box
            className="glare-overlay"
            sx={{
              position: 'absolute',
              top: 0, left: 0, bottom: 0, right: 0,
              pointerEvents: 'none',
              opacity: 0,
              transition: 'opacity 0.3s ease',
              mixBlendMode: 'overlay',
              zIndex: 3,
            }}
          />
        )}
      </Box>
      {!isDisabled && (
        <Box
          className="glare-overlay"
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, height: { sm: '444px', xs: 'auto' },
            borderRadius: '24px',
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            mixBlendMode: 'overlay',
            zIndex: 3,
          }}
        />
      )}
    </Box>
  )

  return (
    <Stack sx={{ minWidth: { sm: '204.6px', xs: '75vw' }, width: { sm: '204.6px', xs: '75vw' } }} alignItems="center" onPointerDown={handlePointerDown}>
      {isDisabled ? (
        <Box sx={{ display: 'block', position: 'relative' }}>
          {ImageContent}
        </Box>
      ) : (
        <Box onClick={handleClick} sx={{ display: 'block', position: 'relative', cursor: 'pointer' }}>
          {ImageContent}
        </Box>
      )}
      
      <Link
        component="span"
        color={isDisabled ? 'text.disabled' : 'interaction'}
        variant="appName"
        underline="none"
        mt={{ md: 3, xs: 8 }}
        mb={1.5}
        sx={{ 
          textAlign: 'center',
          cursor: isDisabled ? 'not-allowed' : 'pointer'
        }}
        onClick={isDisabled ? undefined : handleClick}
      >
        {app.name}
      </Link>
      <Typography variant="appDescription" sx={{ textAlign: 'center', width: { sm: '204.6px', xs: '60vw' }, color: isDisabled ? 'text.disabled' : 'inherit' }}>
        {app.description}
      </Typography>
    </Stack>
  )
}

export default Apps
